import { inject, Service } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Checklist } from '../modules/checklist/types/ChecklistTypes';
import { ChecklistHttpService } from './checklist-http.service';
import { ChecklistStoreService } from './checklist-store.service';
import { ChecklistLoaderService } from './checklist-loader.service';
import { ChecklistLifecycleService } from './checklist-lifecycle.service';
import { ChecklistWriteSenderService } from './checklist-write-sender.service';
import { DepartmentAccessService } from './department-access.service';
import {
  RepeatableMaterializerService,
  WriteTarget,
} from './repeatable-materializer.service';
import { NetworkStatusService } from './offline/network-status.service';
import { ChecklistItemStatus } from '../modules/checklist/types/ChecklistItemStatus.enum';
import { OfflineStorageService } from './offline/offline-storage.service';
import { TimezoneService } from './timezone.service';
import {
  ConditionalWrite,
  ItemWriteConflictService,
} from './item-write-conflict.service';
import {
  clearedItemPatch,
  createUpdatedChecklist,
  customInputPayloadPatch,
  findItem,
  patchItemInAnySection,
  serializeCustomInputPayload,
  withoutItem,
} from '../modules/checklist/utils/checklist-patches';

export interface PreviousCustomInputPayload {
  payload: unknown;
  kind: 'text' | 'number';
  multiline?: boolean;
}

@Service()
export class ChecklistItemWriterService {
  private readonly store = inject(ChecklistStoreService);
  private readonly loader = inject(ChecklistLoaderService);
  private readonly writeSender = inject(ChecklistWriteSenderService);
  private readonly materializer = inject(RepeatableMaterializerService);
  private readonly lifecycle = inject(ChecklistLifecycleService);
  private readonly departmentAccess = inject(DepartmentAccessService);

  private readonly checklistHttpService = inject(ChecklistHttpService);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly offlineStorageService = inject(OfflineStorageService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly itemWriteConflictService = inject(ItemWriteConflictService);

  async updateChecklistItemResolution(
    checklist: Checklist,
    sectionId: string,
    itemId: string,
    status: ChecklistItemStatus,
    completionTime: string,
    entryTime: string | null
  ): Promise<void> {
    const target = await this.resolvePreparedTarget(
      checklist,
      sectionId,
      itemId,
      { promote: status !== ChecklistItemStatus.Unchecked }
    );

    const isOnline = await this.writeSender.sendOrQueue(
      target.checklist.id,
      this.checklistHttpService.updateChecklistItemStatus,
      target.checklist.id,
      target.sectionId,
      target.itemId,
      status,
      completionTime
    );

    const latest = this.latestChecklistFor(target);
    const targetItem = findItem(latest, target.sectionId, target.itemId);

    const updatedChecklist = createUpdatedChecklist(
      latest,
      target.sectionId,
      target.itemId,
      {
        status,
        statusChanged: completionTime,
        // Matches the backend precedence undoTime ?? entryTime ?? statusChanged.
        completionTime: targetItem?.undoTime ?? entryTime ?? completionTime,
      }
    );

    this.store.update(updatedChecklist);

    if (isOnline) {
      this.loader.refreshChecklistAfterWrite(target.checklist.id);
    }
  }

  async clearChecklistItemContents(
    checklist: Checklist,
    sectionId: string,
    itemId: string
  ): Promise<void> {
    const target = await this.resolvePreparedTarget(
      checklist,
      sectionId,
      itemId,
      { promote: false }
    );

    const isOnline = await this.writeSender.sendOrQueue(
      target.checklist.id,
      this.checklistHttpService.clearChecklistItem,
      target.checklist.id,
      target.sectionId,
      target.itemId
    );

    const latest = this.latestChecklistFor(target);
    const targetItem = findItem(latest, target.sectionId, target.itemId);

    this.store.update(
      createUpdatedChecklist(
        latest,
        target.sectionId,
        target.itemId,
        clearedItemPatch(targetItem)
      )
    );

    if (isOnline) {
      this.loader.refreshChecklistAfterWrite(target.checklist.id);
    }
  }

  async keepUndoChecklistItemContents(
    checklist: Checklist,
    sectionId: string,
    itemId: string
  ): Promise<void> {
    const target = await this.resolvePreparedTarget(
      checklist,
      sectionId,
      itemId,
      { promote: false }
    );

    const isOnline = await this.writeSender.sendOrQueue(
      target.checklist.id,
      this.checklistHttpService.undoKeepChecklistItem,
      target.checklist.id,
      target.sectionId,
      target.itemId
    );

    const latest = this.latestChecklistFor(target);
    const targetItem = findItem(latest, target.sectionId, target.itemId);

    // A manual time (entry_time set) stays in entry_time so its "manually edited" indicator survives.
    // An automatic stamp moves to undo_time. Exactly one slot is populated (mirrors the backend).
    const preservedTime = targetItem?.completionTime ?? undefined;
    const manuallyEdited = targetItem?.entryTime != null;

    const updatedChecklist = createUpdatedChecklist(
      latest,
      target.sectionId,
      target.itemId,
      {
        status: ChecklistItemStatus.Unchecked,
        statusChanged: undefined,
        entryTime: manuallyEdited ? targetItem?.entryTime : undefined,
        undoTime: manuallyEdited ? undefined : preservedTime,
        completionTime: preservedTime,
        statusChangedBy: undefined,
      }
    );

    this.store.update(updatedChecklist);

    if (isOnline) {
      this.loader.refreshChecklistAfterWrite(target.checklist.id);
    }
  }

  private async resolvePreparedTarget(
    checklist: Checklist,
    sectionId: string,
    itemId: string,
    { promote }: { promote: boolean } = { promote: true }
  ): Promise<WriteTarget> {
    this.departmentAccess.ensureAccess(checklist.department);
    try {
      if (promote && !this.store.isIsolatedPreview(checklist.id)) {
        await this.lifecycle.promotePreviewIfNeeded(checklist.id);
      }
      const resolved = await this.materializer.resolveTargetForWrite(
        checklist,
        sectionId,
        itemId
      );
      this.materializer.requireOccurrenceItemId(
        resolved.checklist,
        resolved.sectionId,
        resolved.itemId
      );
      return resolved;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  private latestChecklistFor(target: WriteTarget): Checklist {
    return (
      this.store.latest(target.checklist.id, target.checklist) ??
      target.checklist
    );
  }

  updateChecklistItemRemarks(
    checklist: Checklist,
    sectionId: string,
    itemId: string,
    remarks: string,
    previousRemarks?: string
  ): Promise<string> {
    return this.updateItemText(
      'remarks',
      checklist,
      sectionId,
      itemId,
      remarks,
      previousRemarks
    );
  }

  updateChecklistItemHeader(
    checklist: Checklist,
    sectionId: string,
    itemId: string,
    header: string,
    previousHeader?: string
  ): Promise<string> {
    return this.updateItemText(
      'header',
      checklist,
      sectionId,
      itemId,
      header,
      previousHeader
    );
  }

  private async updateItemText(
    field: 'remarks' | 'header',
    checklist: Checklist,
    sectionId: string,
    itemId: string,
    text: string,
    previousText?: string
  ): Promise<string> {
    const target = await this.resolvePreparedTarget(
      checklist,
      sectionId,
      itemId
    );
    const method =
      field === 'remarks'
        ? this.checklistHttpService.updateChecklistItemRemarks
        : this.checklistHttpService.updateChecklistItemHeader;

    let landed = text;
    const isolated = this.store.isIsolatedPreview(target.checklist.id);
    if (!isolated && this.networkStatusService.online()) {
      landed = await this.writeOnline<string>(
        (value, previous) =>
          method.call(
            this.checklistHttpService,
            target.checklist.id,
            target.sectionId,
            target.itemId,
            value,
            previous
          ),
        text,
        previousText === undefined
          ? null
          : send =>
              this.itemWriteConflictService.writeText({
                send,
                local: text,
                previous: previousText,
                multiline: field === 'remarks',
              })
      );
    } else if (!isolated) {
      await this.offlineStorageService.addAction(
        method,
        target.checklist.id,
        target.sectionId,
        target.itemId,
        text,
        previousText
      );
    }

    this.store.update(
      createUpdatedChecklist(
        this.latestChecklistFor(target),
        target.sectionId,
        target.itemId,
        { [field]: landed }
      )
    );
    return landed;
  }

  private async writeOnline<V>(
    send: ConditionalWrite<V>,
    local: V,
    writeWithConflict:
      | ((send: ConditionalWrite<V>) => Promise<{ landed: V }>)
      | null
  ): Promise<V> {
    const write = writeWithConflict
      ? writeWithConflict(send).then(result => result.landed)
      : firstValueFrom(send(local)).then(() => local);
    return write.catch(error => {
      console.error(error);
      throw error;
    });
  }

  async updateChecklistItemCompletionTime(
    checklist: Checklist,
    sectionId: string,
    itemId: string,
    completionTime: string | null
  ): Promise<void> {
    const target = await this.resolvePreparedTarget(
      checklist,
      sectionId,
      itemId
    );

    await this.writeSender.sendOrQueue(
      target.checklist.id,
      this.checklistHttpService.updateChecklistItemCompletionTime,
      target.checklist.id,
      target.sectionId,
      target.itemId,
      completionTime
    );

    const updatedChecklist = createUpdatedChecklist(
      this.latestChecklistFor(target),
      target.sectionId,
      target.itemId,
      {
        completionTime: completionTime ?? undefined,
        entryTime: completionTime ?? undefined,
        // A manual edit clears any undo-preserved time (mirrors the backend).
        undoTime: undefined,
      }
    );

    this.store.update(updatedChecklist);
  }

  async addRepeatableChecklistItem(
    checklist: Checklist,
    sectionId: string,
    itemId: string
  ): Promise<void> {
    this.departmentAccess.ensureAccess(checklist.department);
    if (!this.store.isIsolatedPreview(checklist.id)) {
      await this.lifecycle.promotePreviewIfNeeded(checklist.id);
    }
    await this.materializer.materializePrototypeIfNeeded(
      checklist,
      sectionId,
      itemId
    );
  }

  async discardRepeatableChecklistItem(
    checklist: Checklist,
    sectionId: string,
    itemId: string
  ): Promise<void> {
    const target = await this.resolvePreparedTarget(
      checklist,
      sectionId,
      itemId,
      { promote: false }
    );

    const isOnline = await this.writeSender.sendOrQueue(
      target.checklist.id,
      this.checklistHttpService.discardRepeatableChecklistItem,
      target.checklist.id,
      target.sectionId,
      target.itemId
    );

    // Discarded occurrences are hidden from every read (see getVisibleSectionItems).
    this.store.update({
      ...withoutItem(
        this.latestChecklistFor(target),
        target.sectionId,
        target.itemId
      ),
      lastModified: this.timezoneService.getZonedTimestampNow(),
    });

    if (isOnline) {
      this.loader.refreshChecklistAfterWrite(target.checklist.id);
    }
  }

  async updateChecklistItemCustomInput(
    checklist: Checklist,
    sectionId: string,
    itemId: string,
    customInputId: string,
    payload: unknown,
    slotId?: string | null,
    previous?: PreviousCustomInputPayload
  ): Promise<unknown> {
    const target = await this.resolvePreparedTarget(
      checklist,
      sectionId,
      itemId
    );
    const resolvedCustomInputId =
      target.customInputIdMap?.[customInputId] ?? customInputId;

    let landed = payload;
    const isolated = this.store.isIsolatedPreview(target.checklist.id);
    if (!isolated && this.networkStatusService.online()) {
      landed = await this.writeOnline<unknown>(
        (value, previousPayload) =>
          this.checklistHttpService.updateChecklistItemCustomInput(
            target.checklist.id,
            target.sectionId,
            target.itemId,
            resolvedCustomInputId,
            value,
            previousPayload
          ),
        payload,
        previous === undefined
          ? null
          : send =>
              this.itemWriteConflictService.writePayload({
                send,
                local: payload,
                previous: previous.payload,
                kind: previous.kind,
                multiline: previous.multiline,
              })
      );
    } else if (!isolated) {
      await this.offlineStorageService.addAction(
        this.checklistHttpService.updateChecklistItemCustomInput,
        target.checklist.id,
        target.sectionId,
        target.itemId,
        resolvedCustomInputId,
        payload,
        previous?.payload
      );
    }

    this.applyCustomInputPayload(
      this.latestChecklistFor(target),
      target.itemId,
      resolvedCustomInputId,
      landed,
      slotId
    );
    return landed;
  }

  updateCustomInputPayloadLocally(
    checklistId: string,
    itemId: string,
    customInputId: string,
    payload: unknown,
    slotId?: string | null
  ): void {
    const checklist = this.store.latest(checklistId);
    if (!checklist) return;

    this.applyCustomInputPayload(
      checklist,
      itemId,
      customInputId,
      payload,
      slotId
    );
  }

  private applyCustomInputPayload(
    checklist: Checklist,
    itemId: string,
    customInputId: string,
    payload: unknown,
    slotId: string | null | undefined
  ): void {
    const serializedPayload = serializeCustomInputPayload(payload);
    this.store.update(
      patchItemInAnySection(checklist, itemId, item =>
        customInputPayloadPatch(item, customInputId, serializedPayload, slotId)
      )
    );
  }
}
